/**
 * Recursive tree-structure renderer for the RelationshipTree field.
 *
 * Renders one node: a left switcher column (caret toggle when the node has
 * children, blank gap otherwise) plus the externally supplied row content
 * via `renderNodeRow`. When expanded and the node has children, recurses
 * into them inside an indented column with a single absolutely-positioned
 * 1px guide line aligned to the switcher center. All structural concerns
 * (indent, branch line, expand toggle) live here; per-row content (tag,
 * record picker, menu, etc.) is owned by NodeRow via `renderNodeRow`.
 *
 * The switcher is the only place the unmodified `tree.toggleExpand` chords
 * (Enter / Space) may be bound: it is a sibling of the row content, never an
 * ancestor, so a keydown typed into the row's record picker cannot reach it. It
 * is a disclosure, not an overlay trigger — it announces `aria-expanded` and
 * deliberately no `aria-haspopup`, which is what keeps `isOverlayOpen` from
 * standing form-field navigation down on the focused caret.
 */
import { FC, ReactNode } from 'react';
import { theme } from 'antd';
import { CaretDownFilled, CaretRightFilled } from '@ant-design/icons';

import type { RelationshipTreeNode } from '#types/System.RelationshipTree.types';

import FlexBox from '@/components/atoms/FlexBox';
import { ariaKeyshortcutsFor, useKeybindings } from '@/keyboard';

type TreeNodeProps = {
  node: RelationshipTreeNode;
  expandedKeys: Set<string>;
  toggleExpand: (key: string) => void;
  renderNodeRow: (node: RelationshipTreeNode) => ReactNode;
};

const TreeNode: FC<TreeNodeProps> = ({
  node,
  expandedKeys,
  toggleExpand,
  renderNodeRow,
}) => {
  const { token } = theme.useToken();
  const hasChildren = !!node.children?.length;
  const isExpanded = expandedKeys.has(node.key);
  const switcherSize = token.controlHeightSM;

  const { onKeyDown: switcherKeyDown } = useKeybindings({
    'tree.toggleExpand': () => toggleExpand(node.key),
  });

  return (
    <FlexBox
      column
      gap={token.marginXXS}
      alignStart
      stretch
      style={{ width: '100%' }}
    >
      <FlexBox gap={token.marginXXS} justifyStart style={{ width: '100%' }}>
        <FlexBox
          justifyCenter
          gap={0}
          noGrow
          style={{
            width: switcherSize,
            height: switcherSize,
            cursor: hasChildren ? 'pointer' : 'default',
            color: token.colorTextSecondary,
            flexShrink: 0,
            userSelect: 'none',
          }}
          onClick={hasChildren ? () => toggleExpand(node.key) : undefined}
          onKeyDown={hasChildren ? switcherKeyDown : undefined}
          role={hasChildren ? 'button' : undefined}
          tabIndex={hasChildren ? 0 : undefined}
          aria-label={hasChildren ? `toggle-${node.key}` : undefined}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-keyshortcuts={
            hasChildren ? ariaKeyshortcutsFor('tree.toggleExpand') : undefined
          }
        >
          {hasChildren &&
            (isExpanded ? (
              <CaretDownFilled style={{ fontSize: token.fontSizeSM }} />
            ) : (
              <CaretRightFilled style={{ fontSize: token.fontSizeSM }} />
            ))}
        </FlexBox>
        {renderNodeRow(node)}
      </FlexBox>
      {isExpanded && hasChildren && (
        <FlexBox
          column
          gap={token.marginXXS}
          alignStart
          style={{
            width: '100%',
            paddingLeft: switcherSize,
            position: 'relative',
          }}
        >
          <span
            aria-hidden
            style={{
              position: 'absolute',
              left: switcherSize / 2,
              top: 0,
              bottom: token.controlHeight / 2,
              width: 1,
              backgroundColor: token.colorBorderSecondary,
            }}
          />
          {node.children!.map((child) => (
            <TreeNode
              key={child.key}
              node={child}
              expandedKeys={expandedKeys}
              toggleExpand={toggleExpand}
              renderNodeRow={renderNodeRow}
            />
          ))}
        </FlexBox>
      )}
    </FlexBox>
  );
};

export default TreeNode;
